'use client';

import { useState } from 'react';
import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

export default function CalendarIntegrationCard({ scheduledEvents }) {
  const [selectedDate, setSelectedDate] = useState(new Date()?.getDate());
  const router = useRouter();

  const currentDate = new Date();
  const currentMonth = currentDate?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  
  const getDaysInMonth = () => {
    const year = currentDate?.getFullYear();
    const month = currentDate?.getMonth();
    const firstDay = new Date(year, month, 1)?.getDay();
    const daysInMonth = new Date(year, month + 1, 0)?.getDate();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(day);
    }
    
    return days;
  };

  const hasEvent = (day) => {
    return scheduledEvents?.some(event => event?.day === day);
  };

  const getEventForDay = (day) => {
    return scheduledEvents?.find(event => event?.day === day);
  };

  const handleEventClick = (event) => {
    // If event is a devotional type and has a slug, navigate to that devotional
    if (event?.type === 'devotional' && event?.devotionalSlug) {
      router?.push(`/devotionals/${event?.devotionalSlug}`);
    }
  };

  return (
    <div className="bg-card rounded-xl p-6 border border-border hover-lift">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-crimson font-semibold text-foreground">Devotional Calendar</h3>
        <Link href="/devotionals" title="View all devotionals">
          <Icon name="CalendarIcon" size={24} className="text-primary hover:text-primary/80 cursor-pointer contemplative-transition" />
        </Link>
      </div>
      <div className="space-y-4">
        <div className="text-center">
          <h4 className="text-sm font-inter font-medium text-foreground">{currentMonth}</h4>
        </div>
        
        <div className="grid grid-cols-7 gap-1 text-center">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
            <div key={day} className="text-xs font-inter text-muted-foreground p-2">
              {day}
            </div>
          ))}
          
          {getDaysInMonth()?.map((day, index) => (
            <div key={index} className="aspect-square">
              {day && (
                <button
                  onClick={() => setSelectedDate(day)}
                  className={`w-full h-full flex items-center justify-center text-xs font-inter rounded-lg contemplative-transition ${
                    day === currentDate?.getDate()
                      ? 'bg-primary text-primary-foreground'
                      : day === selectedDate
                      ? 'bg-primary/20 text-primary'
                      : hasEvent(day)
                      ? 'bg-accent/20 text-accent' :'text-foreground hover:bg-surface'
                  }`}
                >
                  {day}
                  {hasEvent(day) && (
                    <div className="absolute w-1 h-1 bg-accent rounded-full mt-4"></div>
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
        
        {selectedDate && getEventForDay(selectedDate) && (
          <div 
            className={`p-3 bg-surface rounded-lg ${
              getEventForDay(selectedDate)?.type === 'devotional' ?'cursor-pointer hover:bg-surface/80 contemplative-transition' :''
            }`}
            onClick={() => handleEventClick(getEventForDay(selectedDate))}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="ClockIcon" size={16} className="text-primary" />
              <span className="text-sm font-inter font-medium text-foreground">
                {getEventForDay(selectedDate)?.title}
              </span>
            </div>
            <p className="text-xs font-inter text-muted-foreground">
              {getEventForDay(selectedDate)?.time} - {getEventForDay(selectedDate)?.description}
            </p>
            {getEventForDay(selectedDate)?.type === 'devotional' && (
              <div className="flex items-center justify-end mt-2">
                <span className="text-xs font-inter text-primary">
                  Click to read devotional →
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

CalendarIntegrationCard.propTypes = {
  scheduledEvents: PropTypes?.arrayOf(
    PropTypes?.shape({
      day: PropTypes?.number?.isRequired,
      title: PropTypes?.string?.isRequired,
      time: PropTypes?.string?.isRequired,
      description: PropTypes?.string?.isRequired,
      type: PropTypes?.string,
      devotionalSlug: PropTypes?.string
    })
  )?.isRequired
};